/**
 * Billing types for the Plan & Coins tab.
 *
 * Implements US-012 / T2: TopupPack interface and isPackPurchasable helper.
 *
 * TO-LL-044 enforcement: field names are exact camelCase keys returned by
 * GET /stripe/topup-packs (BUG-20260703-012526: repointed from the config-only
 * /system-config/topup-packs to the live-priced Stripe route - 2026-07-03) —
 * no snake_case, no renamed keys.
 *
 * CCM-04: packs are rendered config-driven from the API response; never hardcoded
 * in the frontend. The isPackPurchasable helper gates Buy buttons on real Stripe
 * price IDs so TBD placeholders never trigger a guaranteed 4xx.
 *
 * CCM-03: these types are consumed only by PlanAndCoinsTab, which mounts only
 * under ParentGuard — students never reach this surface.
 */

/**
 * A single top-up pack definition returned by GET /stripe/topup-packs.
 *
 * TO-LL-044: exact camelCase key names as returned by the backend.
 *   packId       — unique identifier for this pack (used in POST /stripe/checkout/topup)
 *   coins        — number of Otter Coins granted on purchase
 *   priceSgd     — price in Singapore Dollars (e.g. 5.00)
 *   stripePriceId — Stripe price ID; may be 'TBD_PRICE_PACK_*' placeholder while
 *                   Stripe price IDs are being provisioned
 *   usualPriceSgd — launch-pricing (2026-07-17): the struck-through "usual price"
 *                   anchor shown above the live price; null/absent when the pack is
 *                   not on launch pricing. Display-only — priceSgd remains the live
 *                   charged price. Mirrors SubscriptionPlan.usualPriceSgd below.
 */
export interface TopupPack {
  packId: string;
  coins: number;
  priceSgd: number;
  stripePriceId: string;
  usualPriceSgd?: number | null;
}

/**
 * A single subscription plan/tier returned by GET /system-config/subscription-plans.
 *
 * TO-LL-044: exact camelCase keys as returned by the backend (see
 * Requirements/PRD-Subscription-Tiers.md §4). The response deliberately does NOT
 * carry stripePriceId — the client sends `planKey` to checkout and the backend
 * maps it to the allowlisted price (never trust a client-supplied price id).
 *
 * CCM-04: plans are rendered config-driven from this response; only the static
 * marketing copy (description / suitable-for / bullets) lives in the frontend,
 * keyed by planKey (see PLAN_COPY in SubscriptionPlans.tsx).
 *   planKey          — 'starter' | 'practice' | 'family' (the client's handle on a plan;
 *                       the Family tier's legacy 'unlimited' alias was renamed to 'family'
 *                       on 2026-07-08 — config + backend + dev data migrated together)
 *   name             — display label ('Starter' / 'Practice' / 'Family' — never
 *                       'Unlimited' or 'Family Plan' to a parent, 2026-07-05)
 *   sessionsPerMonth — a positive int for every current plan (Family is metered at 50,
 *                       not uncapped); null is reserved schema-wise for a future truly
 *                       unlimited tier (see `unlimited` below)
 *   priceSgd         — monthly display price in Singapore Dollars
 *   recommended      — highlight this plan (exactly one true — Practice)
 *   unlimited        — true only for a truly uncapped tier; false for every current plan
 *   usualPriceSgd    — launch-pricing sprint (2026-07-16): the struck-through "usual
 *                      price" anchor shown above the launch price; null/absent when the
 *                      plan is not on launch pricing. Display-only — priceSgd remains the
 *                      live charged price.
 */
export interface SubscriptionPlan {
  planKey: string;
  name: string;
  sessionsPerMonth: number | null;
  priceSgd: number;
  recommended: boolean;
  unlimited: boolean;
  usualPriceSgd?: number | null;
}

/**
 * Response shape from GET /system-config/subscription-plans.
 * TO-LL-044: exact camelCase keys 'plans' / 'statementDescriptor'.
 */
export interface SubscriptionPlansResponse {
  plans: SubscriptionPlan[];
  statementDescriptor: string;
}

/**
 * Response shape from POST /stripe/checkout/subscription and POST /stripe/checkout/topup.
 * The frontend reads checkoutUrl and sets window.location.href to redirect.
 *
 * TO-LL-044: exact camelCase key name 'checkoutUrl'.
 */
export interface StripeCheckoutResponse {
  checkoutUrl: string;
  sessionId?: string;
}

/**
 * Response shape from POST /stripe/portal.
 * The frontend reads portalUrl and opens it in a new tab via window.open.
 *
 * TO-LL-044: exact camelCase key name 'portalUrl'.
 */
export interface BillingPortalResponse {
  portalUrl: string;
}

/**
 * Returns true when a pack has a real Stripe price ID that can be passed to
 * POST /stripe/checkout/topup without causing a guaranteed 4xx.
 *
 * Packs with a TBD_PRICE_PACK_* stripePriceId render a disabled 'Coming soon'
 * button — TS-011 rejects TBD values and would return a 4xx on click (AC-5).
 *
 * Implements US-012 / T2. Enforces KB TO-LL-036 indirectly: prevents a silent
 * error path where a click on a TBD pack would always fail.
 */
export function isPackPurchasable(pack: TopupPack): boolean {
  return !pack.stripePriceId.startsWith('TBD_');
}
